import React, { Suspense } from 'react'

import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom'

import { Loading } from '@elgatoylacaja/components'
import { compose } from 'lodash/fp'
import { AppContext, AppContextData } from './contexts/AppContext'

const LazyDemoRoutes = React.lazy(() =>
  import(/* webpackChunkName: "DemoRoutes" */ './DemoRoutes')
)

const Terms = React.lazy(() =>
  import(/* webpackChunkName: "Terms" */ './views/Terms')
)
const Intro = React.lazy(() =>
  import(/* webpackChunkName: "Intro" */ './views/Intro')
)

const Parte1 = React.lazy(() =>
  import(/* webpackChunkName: "Parte1" */ './views/Parte1')
)
const Parte2 = React.lazy(() =>
  import(/* webpackChunkName: "Parte2" */ './views/Parte2')
)
const Parte3 = React.lazy(() =>
  import(/* webpackChunkName: "Parte3" */ './views/Parte3')
)
const Parte4 = React.lazy(() =>
  import(/* webpackChunkName: "Parte4" */ './views/Parte4')
)
const Parte5 = React.lazy(() =>
  import(/* webpackChunkName: "Parte5" */ './views/Parte5')
)

const Regressors = React.lazy(() =>
  import(/* webpackChunkName: "Regressors" */ './views/Regressors')
)

const Thanks = React.lazy(() =>
  import(/* webpackChunkName: "Thanks" */ './views/Thanks')
)

type WrappedComponentType =
  | React.ComponentType<RouteComponentProps<any>>
  | React.ComponentType<any>

const condition = (appState: AppContextData) => (
  f: (x: AppContextData) => boolean
) => (r: string) => (WrappedComponent: WrappedComponentType) => (
  p: RouteComponentProps
) => {
  return p.match.isExact ? (
    f(appState) ? (
      <WrappedComponent {...p} />
    ) : (
      <Redirect to={r} {...p} />
    )
  ) : null
}

type LinkedRouteConfig = { prev?: string; route: string; next?: string }

class LinkedRoutes {
  paths: Array<[string, React.ComponentType<any>]>

  constructor(paths: Array<[string, React.ComponentType<any>]>) {
    this.paths = paths
  }

  config = () =>
    this.paths.map(([path, component], index, list): [
      LinkedRouteConfig,
      React.ComponentType
    ] => [
      {
        prev: index === 0 ? undefined : list[index - 1][0],
        route: path,
        next: index === list.length - 1 ? undefined : list[index + 1][0],
      },
      component,
    ])
}

const ExperimentRoutes = new LinkedRoutes([
  ['/terms', Terms],
  ['/intro', Intro],
  ['/step-1', Parte1],
  ['/step-2', Parte2],
  ['/step-3', Parte3],
  ['/step-4', Parte4],
  ['/step-5', Parte5],
  ['/regressors', Regressors],
  ['/thanks', Thanks],
]).config()

const LazySwitchRoutes = () => {
  const { appState } = React.useContext(AppContext)

  const stateCondition = condition(appState)

  const alwaysTrue = stateCondition(_ => true)('')

  const visited = (r: string) =>
    stateCondition(state => state.visited.includes(r))(r)

  const notVisited = (r: string) =>
    stateCondition(state => !state.visited.includes(r))

  const requirePrevious = ({ prev, route, next }: LinkedRouteConfig) =>
    compose(
      prev !== undefined ? visited(prev) : alwaysTrue,
      next !== undefined ? notVisited(route)(next) : alwaysTrue
    )

  return (
    <Switch>
      <Redirect from="/" exact to="/terms" />
      <Suspense fallback={<Loading loading fixed />}>
        {ExperimentRoutes.map(([config, component]) => (
          <Route
            key={config.route}
            path={config.route}
            exact
            render={requirePrevious(config)(component)}
          />
        ))}
        {process.env.REACT_APP_ENVIRONMENT !== 'production' ? (
          <LazyDemoRoutes appState={appState} />
        ) : null}
      </Suspense>
    </Switch>
  )
}

export default LazySwitchRoutes
